import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Model from "@tripian/model";
import { SvgIcons } from "@tripian/react";
import ValaiDateRangePicker from "../ValaiLanding/components/ValaiDateRangePicker";

export interface ToursAndTicketsDestination {
  destinationId: number;
  destinationName: string;
  coordinate: Model.Coordinate;
  parentName: string;
}

interface IToursAndTicketsSearchForm {
  destinations: ToursAndTicketsDestination[];
  loadingDestinations: boolean;
  profile: Model.TourAndTickets;
  onProfileChange: (profile: Model.TourAndTickets) => void;
  onSubmit: () => void;
  t: (value: Model.TranslationKey) => string;
}

/** Uzun şehir listesinde açılır kutuyu makul tutmak için gösterilen sonuç sayısı */
const MAX_VISIBLE_DESTINATIONS = 60;

/** Segmentlerin ortak görünümü — tarih seçicinin tetikleyicisiyle aynı dil */
const SEGMENT_CLASSES =
  "flex items-center gap-3 w-full px-4 py-[0.7rem] bg-white border-2 border-gray-300 text-left rounded-[30px]";

const ToursAndTicketsSearchForm: React.FC<IToursAndTicketsSearchForm> = ({
  destinations,
  loadingDestinations,
  profile,
  onProfileChange,
  onSubmit,
  t,
}) => {
  const [destinationQuery, setDestinationQuery] = useState("");
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [travellersOpen, setTravellersOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const destinationRef = useRef<HTMLDivElement>(null);
  const travellersRef = useRef<HTMLDivElement>(null);

  // Dışına tıklanınca açılır panelleri kapat
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (destinationRef.current && !destinationRef.current.contains(target)) {
        setDestinationOpen(false);
      }
      if (travellersRef.current && !travellersRef.current.contains(target)) {
        setTravellersOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDestinationOpen(false);
        setTravellersOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const sortedDestinations = useMemo(
    () =>
      [...destinations].sort((a, b) =>
        a.destinationName.localeCompare(b.destinationName)
      ),
    [destinations]
  );

  const filteredDestinations = useMemo(() => {
    const query = destinationQuery.trim().toLocaleLowerCase();
    const matches = query
      ? sortedDestinations.filter(
          destination =>
            destination.destinationName.toLocaleLowerCase().includes(query) ||
            destination.parentName.toLocaleLowerCase().includes(query)
        )
      : sortedDestinations;
    return matches.slice(0, MAX_VISIBLE_DESTINATIONS);
  }, [sortedDestinations, destinationQuery]);

  const selectDestination = (destination: ToursAndTicketsDestination) => {
    onProfileChange({
      ...profile,
      cityId: destination.destinationId,
      cityName: destination.destinationName,
      lat: destination.coordinate.lat,
      lng: destination.coordinate.lng,
    });
    setDestinationQuery("");
    setDestinationOpen(false);
  };

  const changeDates = (startDate: moment.Moment, endDate: moment.Moment) => {
    onProfileChange({
      ...profile,
      arrivalDatetime: startDate.format("YYYY-MM-DD"),
      departureDatetime: endDate.format("YYYY-MM-DD"),
    });
  };

  const changeTravellers = (adult: number, children: number) => {
    onProfileChange({ ...profile, adult, children });
  };

  /**
   * Şehir seçilmemişse alanı kırmızı çerçeveyle işaretleyip listeyi açar. Bu durum
   * için uygun bir çeviri anahtarı olmadığından metin mesajı basılmıyor; uydurma
   * ya da iki anahtarı birleştirerek üretilmiş kopya yerine görsel geri bildirim
   * tercih edildi.
   */
  const submit = () => {
    setSubmitAttempted(true);
    if (!profile.cityId) {
      setDestinationOpen(true);
      return;
    }
    onSubmit();
  };

  const adults = profile.adult || 1;
  const childrenCount = profile.children || 0;

  const travellersSummary = `${adults} ${t(
    "trips.toursAndTickets.adults.label"
  )}, ${childrenCount} ${t("trips.toursAndTickets.children.label")}`;

  // Şehir alanı, seçim yapılmışken şehir adını gösterir; kullanıcı yazmaya
  // başladığında aramaya döner.
  const destinationValue = destinationOpen
    ? destinationQuery
    : profile.cityName || "";

  const missingDestination = submitAttempted && !profile.cityId;

  const counter = (
    label: string,
    value: number,
    minValue: number,
    onChange: (next: number) => void
  ) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`${label} -`}
          disabled={value <= minValue}
          onClick={() => onChange(value - 1)}
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          –
        </button>
        <span className="w-6 text-center text-sm font-medium">{value}</span>
        <button
          type="button"
          aria-label={`${label} +`}
          disabled={value >= 20}
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          +
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-2">
        {/* Nereye */}
        <div ref={destinationRef} className="relative flex-1 min-w-0">
          {loadingDestinations ? (
            <div
              className={`${SEGMENT_CLASSES} animate-pulse border-gray-200`}
              aria-busy="true"
            >
              <SvgIcons.MapPin />
              <span className="h-4 w-28 bg-gray-200 rounded" />
            </div>
          ) : (
            <div
              className={`${SEGMENT_CLASSES} ${
                missingDestination ? "border-red-400" : ""
              }`}
            >
              <SvgIcons.MapPin />
              <input
                type="text"
                value={destinationValue}
                placeholder={t("trips.toursAndTickets.destination.placeholder")}
                onFocus={() => setDestinationOpen(true)}
                onChange={event => {
                  setDestinationQuery(event.target.value);
                  setDestinationOpen(true);
                }}
                className="w-full bg-transparent outline-none text-sm placeholder-gray-500"
              />
            </div>
          )}

          {destinationOpen && !loadingDestinations && (
            <div className="absolute left-0 right-0 top-full mt-2 z-30 bg-white rounded-2xl shadow-xl border border-gray-200 max-h-72 overflow-y-auto">
              {filteredDestinations.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  {t("trips.toursAndTickets.error.notAvailable")}
                </div>
              ) : (
                filteredDestinations.map(destination => (
                  <button
                    key={destination.destinationId}
                    type="button"
                    onClick={() => selectDestination(destination)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 ${
                      destination.destinationId === profile.cityId
                        ? "bg-gray-50"
                        : ""
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-800">
                      {destination.destinationName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {destination.parentName}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Tarihler */}
        <ValaiDateRangePicker
          startDate={moment(profile.arrivalDatetime)}
          endDate={moment(profile.departureDatetime)}
          onDateChange={changeDates}
          className="flex-1 min-w-0"
          hideTimeSelect
        />

        {/* Yolcular */}
        <div ref={travellersRef} className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setTravellersOpen(!travellersOpen)}
            className={SEGMENT_CLASSES}
          >
            <SvgIcons.Users />
            <span className="truncate text-sm">{travellersSummary}</span>
          </button>

          {travellersOpen && (
            <div className="absolute left-0 right-0 top-full mt-2 z-30 bg-white rounded-2xl shadow-xl border border-gray-200 px-4 py-2">
              {counter(
                t("trips.toursAndTickets.adults.title"),
                adults,
                1,
                next => changeTravellers(next, childrenCount)
              )}
              {counter(
                t("trips.toursAndTickets.children.title"),
                childrenCount,
                0,
                next => changeTravellers(adults, next)
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={submit}
          className="flex items-center justify-center gap-2 px-8 py-[0.8rem] rounded-[30px] bg-primary-color text-white font-medium hover:opacity-90 transition-opacity"
        >
          <SvgIcons.Search />
          {t("trips.toursAndTickets.submit")}
        </button>
      </div>

    </div>
  );
};

export default ToursAndTicketsSearchForm;
